/**
 * src/features/printing/components/KitchenSendPrintButton.tsx
 *
 * Bouton "Imprimer KOT" tout-en-un pour la grille d'actions Caisse.tsx.
 *
 * Distinct du bouton "Cuisine" classique (qui ne fait que l'INSERT) :
 * ce bouton garantit l'impression côté caisse via picker imprimante, sans
 * dépendre de l'auto-print KDS realtime.
 *
 * Au clic :
 *   1. Vérifie panier non vide
 *   2. Ouvre le picker imprimante (dialog systématique)
 *   3. Au confirm picker :
 *      a. Broadcast anti-double KDS (skip auto-print sur cet order_number)
 *      b. Appelle onSendKitchen (INSERT kitchen_orders côté parent)
 *      c. Imprime le KOT via PrintingApi.printKitchenTicket avec printerId
 *      d. Appelle onHold (optionnel) pour mettre le panier en attente
 *
 * Invariants :
 *   4.A.15 — payload broadcast/print = orderNumber + items sans PII
 *   4.B.4  — 0 any
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { KitchenPrinterPickerDialog } from './KitchenPrinterPickerDialog';
import { broadcastKitchenPrintSkip } from '../lib/kitchenPrintBroadcast';
import { printKotWithRetryToast } from '../lib/kotPrintRetry';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';

export type KitchenSendPrintItem = {
  productId: string;
  name: string;
  quantity: number;
  notes?: string;
};

export type KitchenSendPrintButtonProps = {
  tenantId: string | null;
  /** Items du panier déjà mappés au format KOT. */
  items: KitchenSendPrintItem[];
  /** Nom du serveur/caissier (alias staff, pas un email). */
  serverName: string;
  /** Numéro de table optionnel. */
  tableNumber?: string;
  /** Destination cible. Défaut : 'CUISINE'. */
  destination?: 'CUISINE' | 'BAR';
  /**
   * Callback INSERT kitchen_orders côté parent.
   * Reçoit l'orderNumber généré ici pour cohérence broadcast + INSERT + print.
   * Doit throw en cas d'erreur.
   */
  onSendKitchen: (args: { orderNumber: string; items: KitchenSendPrintItem[] }) => Promise<void>;
  /** Callback hold/mise en attente du panier (optionnel). */
  onHold?: () => void;
  /** className override pour adapter à la grille parent. */
  className?: string;
  /** Désactivation externe (panier vide géré en interne). */
  disabled?: boolean;
};

function generateOrderNumber(): string {
  return `KOT-${Date.now().toString(36).toUpperCase()}`;
}

export function KitchenSendPrintButton({
  tenantId,
  items,
  serverName,
  tableNumber,
  destination = 'CUISINE',
  onSendKitchen,
  onHold,
  className,
  disabled = false,
}: KitchenSendPrintButtonProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Idempotence persistante des bons KOT (lot 4 CHR, opt-in par tenant).
  const { isFeatureEnabled } = useFeatureToggles();
  const kotIdempotence = isFeatureEnabled('chr_kot_idempotence');

  const handleClick = () => {
    if (!tenantId) {
      toast.error('Tenant non chargé');
      return;
    }
    if (items.length === 0) {
      toast.error('Le panier est vide');
      return;
    }
    setPickerOpen(true);
  };

  const handleConfirm = async (printerId: string) => {
    if (!tenantId || items.length === 0) return;
    setBusy(true);
    const orderNumber = generateOrderNumber();
    try {
      // 1. Broadcast skip AVANT INSERT pour empêcher l'auto-print KDS doublon.
      await broadcastKitchenPrintSkip(tenantId, orderNumber);

      // 2. INSERT kitchen_orders côté parent (déclenche le KDS via realtime).
      try {
        await onSendKitchen({ orderNumber, items });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Envoi cuisine KO : ${msg}`);
        return;
      }

      // 3. Impression KOT garantie via picker — idempotence persistante (lot 4) :
      // clé = orderNumber unique par envoi, toast « Réessayer » sur échec.
      const printed = await printKotWithRetryToast({
        tenantId,
        payload: { destination, tableNumber, orderNumber, serverName, items },
        printerId,
        idempotencyKey: kotIdempotence ? orderNumber : undefined,
        label: destination === 'BAR' ? 'bar' : 'cuisine',
      });
      if (printed) {
        toast.success('KOT envoyé et imprimé', { icon: '🖨️' });
      }

      // 4. Hold du panier (optionnel).
      if (onHold) onHold();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled || busy}
        className={cn('h-24 flex-col gap-1.5 bg-sky-700 text-white hover:bg-sky-800', className)}
      >
        <Printer className="h-7 w-7" />
        <span className="text-sm font-semibold">Imprimer KOT</span>
      </Button>
      <KitchenPrinterPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onConfirm={(printerId) => {
          void handleConfirm(printerId);
        }}
        title="Envoyer + Imprimer KOT"
      />
    </>
  );
}

export default KitchenSendPrintButton;
